import React, { useEffect, useRef } from 'react';

import { useVirtualVenueState, useVirtualVenueActions } from './VirtualVenueContext';
import VirtualVenueLoader from './VirtualVenueLoader';

import styles from './VirtualVenue.module.scss';

const VirtualVenue = ({ meeting, attendee }) => {
  const state = useVirtualVenueState();
  const {
    setDeviceChangeObserverIsSet,
    setAudioVideoObserverIsSet,
    setAttendeePresenceCallbackIsSet,
    setMeetingIsActive,
    lockTile,
    unlockTile,
  } = useVirtualVenueActions();

  const tileIndexRef = useRef(state?.virtualVenue?.tileIndex);
  const audioVideoObserverRef = useRef(null);

  // Update ref when state changes
  useEffect(() => {
    tileIndexRef.current = state?.virtualVenue?.tileIndex;
  }, [state?.virtualVenue?.tileIndex]);

  // Set up device change observer
  useEffect(() => {
    if (!!state?.virtualVenue?.meetingSession && !state?.virtualVenue?.deviceChangeObserverIsSet) {
      const enumerateDeviceList = (deviceList) => {
        deviceList.forEach(device => {
          console.log(`ID: ${device.deviceId} Label: ${device.label}`);
        })
      };

      state.virtualVenue.meetingSession.audioVideo.addDeviceChangeObserver({
        audioInputsChanged: updatedAudioInputDeviceList => {
          enumerateDeviceList(updatedAudioInputDeviceList);
        },

        audioOutputsChanged: updatedAudioOutputDeviceList => {
          enumerateDeviceList(updatedAudioOutputDeviceList);
        },

        videoInputsChanged: updatedVideoInputDeviceList => {
          enumerateDeviceList(updatedVideoInputDeviceList);
        },

        audioInputMuteStateChanged: (device, muted) => {
          console.log('Device', device, muted ? 'is muted' : 'is unmuted');
        },
      });

      setDeviceChangeObserverIsSet(true);
    }
  }, [state?.virtualVenue?.meetingSession, state?.virtualVenue?.deviceChangeObserverIsSet]);

  // Set up audio/video observer
  useEffect(() => {
    if (!!state?.virtualVenue?.meetingSession && !state?.virtualVenue?.audioVideoObserverIsSet) {
      const videoElements = document.getElementsByClassName(styles.video);

      const lockAndReturnVideoElement = tileId => {
        const currentTileIndex = tileIndexRef.current;
        if (currentTileIndex.includes(tileId)) {
          const videoElementIndex = currentTileIndex.indexOf(tileId);
          return videoElements[videoElementIndex];
        }

        if (currentTileIndex.includes(null)) {
          const availableVideoElementIndex = currentTileIndex.indexOf(null);
          console.log("preparing to lock video element", availableVideoElementIndex);
          lockTile(tileId);
          videoElements[availableVideoElementIndex].classList.remove(styles.hidden);
          return videoElements[availableVideoElementIndex];
        }

        throw new Error('Unable to retrieve an available video element');
      };

      const unlockVideoElement = tileId => {
        const currentTileIndex = tileIndexRef.current;
        if (currentTileIndex.includes(tileId)) {
          const videoElementIndexToUnlock = currentTileIndex.indexOf(tileId);
          console.log("preparing to unlock video element", videoElementIndexToUnlock);
          unlockTile(tileId);
          videoElements[videoElementIndexToUnlock].classList.add(styles.hidden);
          return;
        }
      };

      const audioVideoObserver = {
        audioVideoDidStart: () => {
          console.log('audio video did start');
        },
        audioVideoDidStop: sessionStatus => {
          console.log('Stopped the audio video with session status code: ', sessionStatus.statusCode());
        },
        audioVideoDidStartConnecting: reconnecting => {
          if (reconnecting) {
            console.log('Having trouble connecting audio video. Attempting to reconnect.');
          }
        },
        videoTileDidUpdate: tileState => {
          console.log(`Video Tile with ID ${tileState.tileId} is being bound to an HTMLVIDEOELEMENT.`);
          state.virtualVenue.meetingSession.audioVideo.bindVideoElement(
            tileState.tileId,
            lockAndReturnVideoElement(tileState.tileId)
          );
        },
        videoTileWasRemoved: tileId => {
          console.log(`Video Tile with ID ${tileId} was removed.`);
          unlockVideoElement(tileId);
        }
      };

      state.virtualVenue.meetingSession.audioVideo.addObserver(audioVideoObserver);
      audioVideoObserverRef.current = audioVideoObserver;

      setAudioVideoObserverIsSet(true);

      return () => {
        if (state?.virtualVenue?.meetingSession && audioVideoObserverRef.current) {
          state.virtualVenue.meetingSession.audioVideo.removeObserver(audioVideoObserverRef.current);
        }
      };
    }
  }, [state?.virtualVenue?.meetingSession, state?.virtualVenue?.audioVideoObserverIsSet]);

  // Set up attendee presence callback
  useEffect(() => {
    if (!!state?.virtualVenue?.meetingSession && !state?.virtualVenue?.attendeePresenceCallbackIsSet) {
      state.virtualVenue.meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(() => {
        return (presentAttendeeId, present) => {
          console.log(`Attendee ID: ${presentAttendeeId} Present: ${present}`);
          if (present) {
            console.log(`Attendee ${presentAttendeeId} joined meeting.`);
          } else {
            console.log(`Attendee ${presentAttendeeId} left meeting.`);
          }
        }
      });

      setAttendeePresenceCallbackIsSet(true);
    }
  }, [state?.virtualVenue?.meetingSession, state?.virtualVenue?.attendeePresenceCallbackIsSet]);

  // Start meeting when ready
  useEffect(() => {
    if (state?.virtualVenue?.audioVideoDevicesAreChosen &&
        state?.virtualVenue?.deviceChangeObserverIsSet &&
        state?.virtualVenue?.audioVideoObserverIsSet &&
        !state?.virtualVenue?.meetingIsActive &&
        state?.virtualVenue?.meetingSession) {
      const audioElement = document.getElementById('audio');
      state.virtualVenue.meetingSession.audioVideo.bindAudioElement(audioElement);

      state.virtualVenue.meetingSession.audioVideo.start();
      state.virtualVenue.meetingSession.audioVideo.startLocalVideoTile();
      setMeetingIsActive(true);
    }
  }, [
    state?.virtualVenue?.audioVideoDevicesAreChosen,
    state?.virtualVenue?.deviceChangeObserverIsSet,
    state?.virtualVenue?.audioVideoObserverIsSet,
    state?.virtualVenue?.meetingIsActive,
    state?.virtualVenue?.meetingSession,
  ]);

  const gridStyle = () => {
    const numberOfActiveTiles = (state?.virtualVenue?.tileIndex || []).filter((tile) => { return tile !== null; }).length;

    if (numberOfActiveTiles === 0) {
      return styles.oneTileGrid;
    } else if (numberOfActiveTiles === 1) {
      return styles.oneTileGrid;
    } else if (numberOfActiveTiles === 2) {
      return styles.twoTileGrid;
    } else if (numberOfActiveTiles === 3) {
      return styles.fourTileGrid;
    } else if (numberOfActiveTiles === 4) {
      return styles.fourTileGrid;
    } else if (numberOfActiveTiles === 5) {
      return styles.sixTileGrid;
    } else if (numberOfActiveTiles === 6) {
      return styles.sixTileGrid;
    } else if (numberOfActiveTiles === 7) {
      return styles.nineTileGrid;
    } else if (numberOfActiveTiles === 8) {
      return styles.nineTileGrid;
    } else if (numberOfActiveTiles === 9) {
      return styles.nineTileGrid;
    }
  };

  return (
    <>
      <VirtualVenueLoader meeting={meeting} attendee={attendee} />
      <div id={styles.videoGrid} className={gridStyle()}>
        {(state?.virtualVenue?.tileIndex || []).map((_, index) => {
          return (<video key={index} className={`${styles.video} ${styles.hidden}`}></video>);
        })}
      </div>
      <audio id="audio"></audio>
    </>
  );
};

export default VirtualVenue;
